import Card from "@imminently/decisively-ui/Card";
import { Handle, Position, type NodeProps } from "@xyflow/react";
import type React from "react";
import type { OrgModelRecordNodeData } from "../types";
import { asString, getIssueCyclePath, getIssueMessage, truncateWithEllipsis } from "../utils";

const NODE_HEADER_ID_MAX_LENGTH = 16;

/** One `dt`/`dd` pair in the record body grid — keeps the ~10 label/value rows below from repeating the same markup. */
const NodeField = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <>
    <dt className="text-muted-foreground">{label}</dt>
    <dd className="min-w-0 truncate text-foreground">{value}</dd>
  </>
);

export const OrgModelGraphRecordNode = (props: NodeProps) => {
  const data = props.data as OrgModelRecordNodeData;
  const { record } = data;
  if (record.type === "hierarchyIssue") {
    return (
      // Deliberately off-theme: this is an alert-style card (orange, not the `Card` token
      // palette) so it reads as a warning at a glance against the neutral record/entity nodes.
      <div className="flex min-h-43 w-93 flex-col overflow-hidden rounded-lg border border-orange-300 bg-orange-50 shadow-[0_2px_8px_rgba(154,52,18,0.12)]">
        <div className="flex items-center justify-between gap-3 border-b border-orange-200 px-3 py-3">
          <h4 className="text-sm font-semibold text-orange-900">Hierarchy issue</h4>
        </div>
        <div className="flex flex-col gap-3 px-3 pb-3 pt-3 text-xs leading-snug">
          <p className="wrap-break-word font-medium text-orange-900">{getIssueMessage(record)}</p>
          <dl>
            <dt className="text-xs font-semibold uppercase tracking-wider text-orange-800">Cycle path</dt>
            <dd className="wrap-break-word font-mono text-xs leading-6 text-orange-950">
              {getIssueCyclePath(record)}
            </dd>
          </dl>
        </div>
        <Handle
          type="target"
          position={Position.Right}
        />
        <Handle
          type="source"
          position={Position.Left}
        />
      </div>
    );
  }

  const recordId = asString(record.id);
  const recordIdLabel = recordId || "-";
  const truncatedRecordId = truncateWithEllipsis(recordIdLabel, NODE_HEADER_ID_MAX_LENGTH);
  const shouldShowRecordIdTooltip = recordIdLabel.length > NODE_HEADER_ID_MAX_LENGTH;

  return (
    <Card
      elevation="sm"
      className="min-h-62 w-85 gap-0 overflow-hidden p-0"
    >
      <div className="flex items-center justify-between border-b border-border px-3 py-3">
        <h4 className="text-sm font-semibold text-foreground">{record.type}</h4>
        <span
          className="max-w-30 truncate text-xs text-muted-foreground"
          title={shouldShowRecordIdTooltip ? recordIdLabel : undefined}
        >
          {truncatedRecordId}
        </span>
      </div>

      <dl className="grid flex-1 grid-cols-[100px_1fr] gap-x-2 gap-y-2 px-3 py-3 font-mono text-xs leading-snug">
        <NodeField
          label="subjectType"
          value={record.subjectType}
        />
        <NodeField
          label="subjectId"
          value={record.subjectId}
        />
        {record.type === "hierarchySummary" ? (
          <>
            <NodeField
              label="parentCount"
              value={record.parentCount ?? "-"}
            />
            <NodeField
              label="childrenCount"
              value={record.childrenCount ?? record.childCount ?? "-"}
            />
          </>
        ) : (
          <>
            <NodeField
              label="relatedId"
              value={record.relatedId}
            />
            <NodeField
              label="level"
              value={record.level ?? "-"}
            />
          </>
        )}
        <NodeField
          label="source"
          value={record.source ?? "-"}
        />
        {record.type === "hierarchySummary" ? null : (
          <>
            <NodeField
              label="validFrom"
              value={record.validFrom ?? "-"}
            />
            <NodeField
              label="validTo"
              value={record.validTo ?? "-"}
            />
          </>
        )}
      </dl>

      <Handle
        type="target"
        position={Position.Right}
      />
      <Handle
        type="source"
        position={Position.Left}
      />
    </Card>
  );
};

export default OrgModelGraphRecordNode;
